import { OptionsObject } from 'notistack';
import { useCallback } from 'react';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useNotification from '@/shared/hooks/UseNotification';
import useEvent from './useEvent';

type BaseOptions<TData, TError, TVariables, TContext> = {
  entityKey?: string;
  getErrorMessage?: (error: TError) => string | null | undefined;
  mutateAsync: UseMutateAsyncFunction<TData, TError, TVariables, TContext>;
  onError?: (error: TError) => void;
  onSuccess?: (data: TData) => void;
  shouldShowErrorSnackbar?: (error: TError) => boolean;
  successVariant?: 'success' | 'info';
};

type CustomOptions = {
  snackbar: {
    error: string;
    success: string;
  };
};

type MutateCallbacks<TData, TError> = {
  onError?: (data: TError) => void;
  onSettled?: (data: TData | undefined, error: TError | undefined) => void;
  onSuccess?: (data: TData) => void;
};

const SNACKBAR_OPTIONS = {
  preventDuplicate: true,
};

const controlledTypes = {
  CANCEL: 'cancel',
  COPY: 'copy',
  CREATE: 'create',
  DEACTIVATE: 'deactivate',
  DELETE: 'delete',
  DUPLICATE: 'duplicate',
  SEND: 'send',
  UPDATE: 'update',
  WILL_BE_PROCESSED: 'willBeProcessed',
} as const;

const messageKeys = {
  error: {
    [controlledTypes.CANCEL]: 'errors:failedToCancelType',
    [controlledTypes.COPY]: 'errors:failedToCopyType',
    [controlledTypes.CREATE]: 'errors:failedToCreateType',
    [controlledTypes.UPDATE]: 'errors:failedToUpdateType',
    [controlledTypes.DELETE]: 'errors:failedToDeleteType',
    [controlledTypes.DUPLICATE]: 'errors:failedToDuplicateType',
    [controlledTypes.SEND]: 'errors:failedToSendType',
    [controlledTypes.WILL_BE_PROCESSED]: 'errors:failedToProcessType',
    [controlledTypes.DEACTIVATE]: 'errors:failedToDeactivateType',
  },
  success: {
    [controlledTypes.CANCEL]: 'typeSuccessfullyCancelled',
    [controlledTypes.COPY]: 'typeSuccessfullyCopied',
    [controlledTypes.CREATE]: 'typeSuccessfullyAdded',
    [controlledTypes.UPDATE]: 'typeSuccessfullyUpdated',
    [controlledTypes.DELETE]: 'typeSuccessfullyRemoved',
    [controlledTypes.DUPLICATE]: 'typeSuccessfullyDuplicated',
    [controlledTypes.SEND]: 'typeSuccessfullySent',
    [controlledTypes.WILL_BE_PROCESSED]: 'typeWillBeProcessed',
    [controlledTypes.DEACTIVATE]: 'typeWillBeDeactivated',
  },
};

type ControlledType = (typeof controlledTypes)[keyof typeof controlledTypes];

type ControlledOptions = {
  actionKey: ControlledType;
};

type Options<TData, TError, TVariables, TContext> = BaseOptions<
  TData,
  TError,
  TVariables,
  TContext
> &
  (ControlledOptions | CustomOptions);

const isControlled = (options: ControlledOptions | CustomOptions): options is ControlledOptions =>
  !!(options as ControlledOptions).actionKey;

const useMutateWithSnackbar = <TData, TError, TVariables, TContext>(
  options: Options<TData, TError, TVariables, TContext>,
  snackbarOptions?: OptionsObject,
) => {
  const { sendNotification } = useNotification();
  const { t } = useTranslation();

  const snackbarKeys = (() => {
    if (!isControlled(options)) return options.snackbar;

    return {
      error: messageKeys.error[options.actionKey],
      success: messageKeys.success[options.actionKey],
    };
  })();

  const {
    entityKey,
    getErrorMessage,
    mutateAsync,
    onError,
    onSuccess,
    shouldShowErrorSnackbar,
    successVariant,
  } = options;

  const handleError = useEvent(onError);
  const resolveErrorMessage = useEvent(getErrorMessage);
  const handleSuccess = useEvent(onSuccess);
  const handleShouldShowErrorSnackbar = useEvent(shouldShowErrorSnackbar);

  const mutateWithSnackbar = useCallback(
    async (input: TVariables, callbacks?: MutateCallbacks<TData, TError>) => {
      const tOptions = { type: entityKey ? t(entityKey) : undefined };

      let castError;
      let res;

      try {
        res = await mutateAsync(input);

        sendNotification({
          message: t(snackbarKeys.success, tOptions),
          options: { ...SNACKBAR_OPTIONS, ...snackbarOptions },
          variant: successVariant || 'success',
        });
        callbacks?.onSuccess?.(res);
        handleSuccess(res);

        return [res, null] as const;
      } catch (err) {
        castError = err as TError;

        if (handleShouldShowErrorSnackbar?.(castError) ?? true) {
          sendNotification({
            message: resolveErrorMessage(castError) || t(snackbarKeys.error, tOptions),
            options: { ...SNACKBAR_OPTIONS, ...snackbarOptions },
            variant: 'error',
          });
        }
        callbacks?.onError?.(castError);
        handleError(castError);

        return [null, castError] as const;
      } finally {
        callbacks?.onSettled?.(res, castError);
      }
    },
    [
      sendNotification,
      entityKey,
      handleError,
      handleSuccess,
      mutateAsync,
      resolveErrorMessage,
      handleShouldShowErrorSnackbar,
      snackbarKeys.error,
      snackbarKeys.success,
      snackbarOptions,
      successVariant,
      t,
    ],
  );

  return mutateWithSnackbar;
};

export default useMutateWithSnackbar;
